import * as store from '@attain-apps/fe/web/main/lib/store'
import { removeUndefinedProperties } from '@attain-apps/object-utils'
import * as rxjs from 'rxjs'

import * as models from '../../models'
import { EncodedQueryParam } from '../../models'
import type { Router } from '../feature'

// TODO: maybe use bufferTime ONLY with encoded query params so that we can
// combine batches of updates to the encoded query params

/**
 * Executes our navigation logic
 */
export const navigate = store.effects.create((_router: Router) =>
  _router.actions.navigate.request.observe().pipe(
    // concatMap ensures all actions get executed, but in the proper order
    rxjs.concatMap(action => {
      /**
       * Converts the result of an Angular routing request (Promise returning a
       * boolean) into the appropriate actions
       */
      function _resultActionFrom(navigationResult: Promise<boolean>) {
        return rxjs.from(navigationResult).pipe(
          // Since the router returns a promise, we expect only one
          // emission. Therefore, we switch from the navigation observable
          // to the action observables.
          rxjs.map(result => {
            // null means navigation is skipped
            if (result === null) {
              return _router.actions.navigate.cancellation.from(action).create({
                reason: 'Navigation was skipped',
                request: action.data,
              })
            }

            // if navigation was successful, dispatch success action,
            // otherwise dispatch error action
            return result // true => success
              ? _router.actions.navigate.success.from(action).create({
                  request: action.data,
                })
              : _router.actions.navigate.error.from(action).create({
                  error: new Error('Failed to navigate'),
                  request: action.data,
                })
          }),
        )
      }

      if (action.data.url) {
        return _resultActionFrom(
          // NOTE: `navigateByUrl` can accept a UrlTree, BUT NgRx freezes the
          // Action.data, and `navigateByUrl` tries to mutate the UrlTree. So,
          // we need to serialize the UrlTree into a string, so that a new,
          // mutable UrlTree is created under the hood
          _router.ngRouter.navigateByUrl(
            typeof action.data.url === 'string'
              ? action.data.url
              : _router.urlSerializer.serialize(action.data.url),
            action.data,
          ),
        )
      }

      const url = _router.urlSerializer.parse(
        // This is effectively everything after origin
        window.location.pathname +
          window.location.search +
          window.location.hash,
      )
      const queryParams = models.Request.synthesizeQueryParams(
        action.data,
        EncodedQueryParam.FullObject.decode(
          (url.queryParams[EncodedQueryParam.KEY] as unknown)?.toString(),
        ),
      )
      if (action.data.path !== undefined) {
        // If we are given a path, we can use the standard `navigate`
        // approach
        return _resultActionFrom(
          _router.ngRouter.navigate(
            // NOTE: Angular has incorrect types. It claims to require a
            // mutable list, but it actually does not
            action.data.path as Mutable<typeof action.data.path>,
            {
              ...action.data,
              queryParams,
              queryParamsHandling: 'merge',
            },
          ),
        )
      }
      // If we are not given a path, we need to maintain the current path. It
      // turns out that is not so easy since that typically requires access to
      // an accurate ActivatedRoute, which is not trivial to retrieve. Instead,
      // we manually modify the current URL
      if (action.data.fragment !== undefined) {
        url.fragment = action.data.fragment
      }
      for (const k in queryParams) {
        const value = queryParams[k]
        if (value === null) {
          delete url.queryParams[k]
        } else if (value !== undefined) {
          url.queryParams[k] = value
        }
      }
      return _resultActionFrom(_router.ngRouter.navigateByUrl(url, action.data))
    }),
  ),
)
